import React, { useState } from 'react';
import { X, Upload, FileText, FileSpreadsheet, Files, Layers, Link2, Bookmark } from 'lucide-react';
import * as XLSX from 'xlsx';
import mammoth from 'mammoth';
import { supabase } from './supabaseClient';
import { UNITS } from './constants';
import { parseTextSmart } from './importParser';

const ImportModal = ({
  isOpen,
  onClose,
  // Для імпорту в звіт (EstimateScreen)
  onImport = null,         // (items, priceType) => void — додати в звіт
  // Для шаблонів/тендерів
  templates,
  setTemplates,
  setFilledCards,
  user,
  // Модалка підтвердження
  setConfirmModal,
  // Режим: 'report' показує кнопку "Імпортувати" + перемикач цін, 'templates' — ні
  mode = 'report'
}) => {
  const [importText, setImportText] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importParsedItems, setImportParsedItems] = useState([]);
  const [importSectionName, setImportSectionName] = useState('Імпортовані позиції');
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState({ stage: '', percent: 0 });
  const [importPriceType, setImportPriceType] = useState('work');

  const resetAndClose = () => {
    setImportText('');
    setImportFile(null);
    setImportParsedItems([]);
    setImportSectionName('Імпортовані позиції');
    setImportPriceType('work');
    setImportProgress({ stage: '', percent: 0 });
    onClose();
  };

  const toggleImportItem = (itemId) => {
    setImportParsedItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, selected: !item.selected } : item
    ));
  };

  // File → base64 (без "data:mime;base64," префіксу — тільки чисті дані)
  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || '';
      const commaIdx = result.indexOf(',');
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  // Універсальний AI-парсер: приймає { text } АБО { fileBase64, mimeType }
  const parseWithAI = async ({ text, fileBase64, mimeType }) => {
    setImportLoading(true);
    setImportProgress({ stage: 'Підготовка...', percent: 10 });

    try {
      setImportProgress({ stage: 'Авторизація...', percent: 20 });
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Не авторизовано');

      setImportProgress({ stage: 'AI розпізнає позиції...', percent: 40 });
      const body = text ? { text } : { fileBase64, mimeType };

      const response = await fetch('https://jkouufahpliqgyvcmbwb.supabase.co/functions/v1/parse-estimate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      setImportProgress({ stage: 'Обробка результатів...', percent: 80 });
      const data = await response.json();

      if (data.items && data.items.length > 0) {
        setImportProgress({ stage: 'Готово!', percent: 100 });
        const itemsWithIds = data.items.map((item, idx) => ({
          ...item,
          id: Date.now() + idx,
          selected: true,
          qty: item.qty || 1,
          workPrice: Math.round((item.price || item.workPrice || 0) * 100) / 100,
          unit: item.unit || 'шт.',
          type: item.type || 'Робота',
          markup: 20,
          priceLock: false
        }));
        setImportParsedItems(itemsWithIds);
      } else if (text) {
        // Fallback до локального парсера — тільки для текстового шляху
        setImportProgress({ stage: 'Локальний парсер...', percent: 90 });
        const items = parseTextSmart(text);
        if (items.length > 0) {
          setImportProgress({ stage: 'Готово!', percent: 100 });
          setImportParsedItems(items);
        } else {
          setConfirmModal({
            show: true,
            title: 'Не вдалось розпізнати',
            message: 'Не вдалось знайти позиції в тексті. Спробуйте інший формат.',
            onConfirm: null
          });
        }
      } else {
        setConfirmModal({
          show: true,
          title: 'Не вдалось розпізнати',
          message: 'AI не знайшов позиції у файлі. Спробуйте кращу якість фото або інший формат.',
          onConfirm: null
        });
      }
    } catch (error) {
      console.error('AI Parse error:', error);
      if (text) {
        setImportProgress({ stage: 'Локальний парсер...', percent: 90 });
        const items = parseTextSmart(text);
        if (items.length > 0) {
          setImportProgress({ stage: 'Готово!', percent: 100 });
          setImportParsedItems(items);
        } else {
          setConfirmModal({
            show: true,
            title: 'Помилка',
            message: 'Не вдалось обробити текст. Спробуйте ще раз.',
            onConfirm: null
          });
        }
      } else {
        setConfirmModal({
          show: true,
          title: 'Помилка',
          message: 'Не вдалось обробити файл. Спробуйте ще раз або інший формат.',
          onConfirm: null
        });
      }
    }

    setImportLoading(false);
    setTimeout(() => setImportProgress({ stage: '', percent: 0 }), 1000);
  };

  // Backward-compat обгортка (використовується для paste-in-textarea та .xlsx/.docx/.csv)
  const parseTextWithAI = (text) => parseWithAI({ text });

  // Файловий імпорт
  const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];
  const handleFileImport = async (file) => {
    setImportLoading(true);
    const extension = file.name.split('.').pop().toLowerCase();

    try {
      if (extension === 'pdf' || IMAGE_EXTS.includes(extension)) {
        // PDF або фото → base64 → Gemini Vision (без клієнтського pdfjs)
        setImportProgress({ stage: 'Читаю файл...', percent: 15 });
        const fileBase64 = await fileToBase64(file);
        const normalizedExt = extension === 'jpg' ? 'jpeg' : extension;
        const mimeType = file.type || (extension === 'pdf' ? 'application/pdf' : `image/${normalizedExt}`);
        await parseWithAI({ fileBase64, mimeType });
      } else if (extension === 'xlsx' || extension === 'xls') {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        const text = jsonData.map(row => row.join(' | ')).join('\n');
        await parseTextWithAI(text);
      } else if (extension === 'docx') {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        await parseTextWithAI(result.value);
      } else if (extension === 'doc') {
        const arrayBuffer = await file.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        let text = '';
        for (let i = 0; i < bytes.length; i++) {
          const byte = bytes[i];
          if ((byte >= 32 && byte <= 126) || byte === 10 || byte === 13) {
            text += String.fromCharCode(byte);
          } else if (byte >= 192 && byte <= 255) {
            text += ' ';
          }
        }
        text = text.replace(/[^\w\s\d.,;:!?()%₴грнГРНшткгмлпосл-]/gi, ' ').replace(/\s+/g, ' ').split(' ').filter(word => word.length > 1).join(' ');
        if (text.trim().length > 10) {
          await parseTextWithAI(text);
        } else {
          setConfirmModal({
            show: true,
            title: 'Формат .doc',
            message: 'Старий формат .doc важко прочитати. Спробуйте зберегти як .docx.',
            onConfirm: null
          });
        }
      } else if (extension === 'csv' || extension === 'txt') {
        const text = await file.text();
        await parseTextWithAI(text);
      }
    } catch (error) {
      console.error('File import error:', error);
      setConfirmModal({
        show: true,
        title: 'Помилка імпорту',
        message: 'Не вдалось прочитати файл. Спробуйте інший формат.',
        onConfirm: null
      });
    }
    setImportLoading(false);
  };

  // Зберегти як шаблон
  const saveAsTemplate = () => {
    setConfirmModal({
      show: true,
      title: 'Зберегти як шаблон',
      message: 'Ціни будуть збережені як для замовника',
      confirmText: 'Добре',
      cancelText: 'Скасувати',
      onConfirm: () => {
        const selected = importParsedItems.filter(i => i.selected);
        if (selected.length === 0) return;

        const templateName = importSectionName || 'Імпортовані позиції';
        const newTemplate = {
          id: Date.now(),
          name: templateName,
          description: '',
          sections: [{
            id: Date.now() + 1,
            name: templateName,
            items: selected.map(item => ({
              id: Date.now() + Math.random(),
              position: item.position,
              unit: UNITS.includes(item.unit) ? item.unit : UNITS[0],
              clientPrice: Math.round((item.workPrice || 0) * 100) / 100,
              type: item.type || 'Робота'
            }))
          }],
          createdAt: new Date().toISOString(),
          usedCount: 0
        };

        setTemplates(prev => [...prev, newTemplate]);
        if (window.showToast) window.showToast(`Шаблон "${templateName}" створено`);
        resetAndClose();
      }
    });
  };

  // Зберегти як тендер
  const saveAsTender = () => {
    setConfirmModal({
      show: true,
      title: 'Зберегти як тендер',
      message: 'Ціни будуть збережені в тендері як робочі',
      confirmText: 'Добре',
      cancelText: 'Скасувати',
      onConfirm: async () => {
        const selected = importParsedItems.filter(i => i.selected);
        if (selected.length === 0) return;

        const tenderName = importSectionName || 'Імпортовані позиції';
        const templateSnapshot = {
          id: Date.now(),
          name: tenderName,
          sections: [{
            id: Date.now() + 1,
            name: tenderName,
            items: selected.map(item => ({
              id: Date.now() + Math.random(),
              position: item.position,
              unit: UNITS.includes(item.unit) ? item.unit : UNITS[0],
              qty: item.qty || 1,
              type: item.type || 'Робота'
            }))
          }]
        };

        try {
          const { data, error } = await supabase
            .from('shared_templates')
            .insert({
              user_id: user.id,
              template_id: String(templateSnapshot.id),
              template_snapshot: templateSnapshot,
              status: 'pending'
            })
            .select()
            .single();

          if (error) throw error;

          const link = `${window.location.origin}/template/${data.share_token}`;
          await navigator.clipboard.writeText(link);
          if (setFilledCards) setFilledCards(prev => [{ ...data, responsesCount: 0 }, ...prev]);
          if (window.showToast) window.showToast('Тендер створено, посилання скопійовано!');
        } catch (e) {
          console.error('Помилка створення тендеру:', e);
          if (window.showToast) window.showToast('Помилка створення тендеру');
        }
        resetAndClose();
      }
    });
  };

  // Зберегти в довідник (нова секція з датою імпорту)
  const saveToDirectory = () => {
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    const sectionName = `Імпортовано ${dd}.${mm}.${yy}`;
    setConfirmModal({
      show: true,
      title: 'Зберегти в довідник',
      message: `Створити новий розділ довідника "${sectionName}"?`,
      confirmText: 'Добре',
      cancelText: 'Скасувати',
      onConfirm: async () => {
        const selected = importParsedItems.filter(i => i.selected);
        if (selected.length === 0) return;
        try {
          const { data: newSec, error: sectionErr } = await supabase
            .from('user_directory')
            .insert({ user_id: user.id, name: sectionName, sort_order: 0 })
            .select()
            .single();
          if (sectionErr) throw sectionErr;

          const rows = selected.map((item, idx) => ({
            section_id: newSec.id,
            user_id: user.id,
            position: item.position,
            unit: UNITS.includes(item.unit) ? item.unit : UNITS[0],
            price: Math.round((item.workPrice || 0) * 100) / 100,
            type: item.type || 'Робота',
            sort_order: idx
          }));
          const { error: itemsErr } = await supabase
            .from('user_directory_items')
            .insert(rows);
          if (itemsErr) throw itemsErr;

          if (window.showToast) window.showToast(`Додано в довідник: "${sectionName}"`);
        } catch (e) {
          console.error('Помилка збереження в довідник:', e);
          if (window.showToast) window.showToast('Помилка збереження в довідник');
        }
        resetAndClose();
      }
    });
  };

  if (!isOpen) return null;

  const selectedCount = importParsedItems.filter(i => i.selected).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white rounded-t-2xl sm:rounded-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="sticky top-0 bg-white z-10 px-4 sm:px-6 pt-4 sm:pt-6 pb-3 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold" style={{ color: '#163248' }}>Імпорт позицій</h3>
            <button onClick={resetAndClose} className="p-1 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">

          {/* Завантаження файлу */}
          <div className="mb-4">
            <div
              className={`p-4 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors ${
                importFile ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-blue-300 hover:bg-blue-50/50'
              }`}
              onClick={() => document.getElementById('import-file-input-modal').click()}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const file = e.dataTransfer.files[0];
                if (file) { setImportFile(file); handleFileImport(file); }
              }}
            >
              <input
                id="import-file-input-modal"
                type="file"
                accept=".pdf,.xlsx,.xls,.doc,.docx,.csv,.txt,.jpg,.jpeg,.png,.webp,.heic,.heif,image/*,application/pdf"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) { setImportFile(file); handleFileImport(file); }
                }}
                className="hidden"
              />
              {importFile ? (
                <div className="flex items-center justify-center gap-2">
                  {importFile.name.endsWith('.pdf') ? (
                    <FileText className="w-5 h-5 text-red-500" />
                  ) : importFile.name.match(/\.xlsx?$/i) ? (
                    <FileSpreadsheet className="w-5 h-5 text-green-600" />
                  ) : importFile.name.match(/\.docx?$/i) ? (
                    <FileText className="w-5 h-5 text-blue-600" />
                  ) : (
                    <Files className="w-5 h-5 text-gray-500" />
                  )}
                  <span className="text-sm text-gray-700">{importFile.name}</span>
                  <button onClick={(e) => { e.stopPropagation(); setImportFile(null); }} className="text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                  <div className="text-xs text-gray-500">Перетягніть або виберіть файл</div>
                  <div className="text-[10px] text-gray-400 mt-0.5">PDF, фото (JPG, PNG, HEIC), Excel, Word, CSV, TXT</div>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4 my-4">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-sm text-gray-400">або вставте текст</span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>

          {/* Текстове поле */}
          <div className="mb-4">
            <textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder={"Вставте текст з месенджера, наприклад:\nМонтаж кондера - 2 шт по 3500\nТруби 15м * 600грн\nРозетки 5шт 250"}
              className="w-full px-4 py-3 border rounded-lg h-32 text-sm"
            />
            <button
              onClick={() => parseTextWithAI(importText)}
              disabled={!importText.trim() || importLoading}
              className="mt-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white rounded-lg text-sm transition-colors"
            >
              {importLoading ? 'Розпізнавання...' : 'Розпізнати позиції'}
            </button>

            {importLoading && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                  <span>{importProgress.stage}</span>
                  <span>{importProgress.percent}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-green-500 h-2 rounded-full transition-all duration-300" style={{ width: `${importProgress.percent}%` }}></div>
                </div>
              </div>
            )}
          </div>

          {/* Попередній перегляд */}
          {importParsedItems.length > 0 && (
            <div className="mb-4">
              {/* Перемикач типу цін — тільки для звітів */}
              {mode === 'report' && (
                <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Тип цін у файлі:</label>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="radio" name="priceType" value="work" checked={importPriceType === 'work'} onChange={(e) => setImportPriceType(e.target.value)} className="w-4 h-4 text-green-600 mt-0.5" />
                      <div>
                        <span className="text-sm font-medium">Робочі ціни</span>
                        <p className="text-xs text-gray-500 mt-0.5">Ціни будуть імпортовані в колонку робочі ціни</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="radio" name="priceType" value="client" checked={importPriceType === 'client'} onChange={(e) => setImportPriceType(e.target.value)} className="w-4 h-4 text-green-600 mt-0.5" />
                      <div>
                        <span className="text-sm font-medium">Ціни для замовника</span>
                        <p className="text-xs text-gray-500 mt-0.5">Ціни будуть імпортовані в колонку для замовника і зафіксовані</p>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              <label className="block text-sm font-medium text-gray-700 mb-2">
                Розпізнані позиції ({selectedCount} з {importParsedItems.length})
              </label>
              <div className="border rounded-lg overflow-hidden max-h-60 overflow-y-auto overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left w-8">
                        <input type="checkbox" checked={importParsedItems.every(i => i.selected)} onChange={(e) => setImportParsedItems(prev => prev.map(item => ({ ...item, selected: e.target.checked })))} className="w-4 h-4" />
                      </th>
                      <th className="px-3 py-2 text-left">Позиція</th>
                      <th className="px-3 py-2 text-right w-16">К-ть</th>
                      <th className="px-3 py-2 text-left w-16">Од.</th>
                      <th className="px-3 py-2 text-right w-24">Ціна</th>
                      <th className="px-3 py-2 text-right w-24">Сума</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importParsedItems.map(item => (
                      <tr key={item.id} className={`border-t ${item.selected ? 'bg-green-50' : 'bg-gray-50 opacity-50'}`}>
                        <td className="px-3 py-2"><input type="checkbox" checked={item.selected} onChange={() => toggleImportItem(item.id)} className="w-4 h-4" /></td>
                        <td className="px-3 py-2">{item.position}</td>
                        <td className="px-3 py-2 text-right">{item.qty}</td>
                        <td className="px-3 py-2">{item.unit}</td>
                        <td className="px-3 py-2 text-right">{item.workPrice?.toFixed(0)}</td>
                        <td className="px-3 py-2 text-right font-medium">{(item.qty * item.workPrice).toFixed(0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Назва розділу або шаблону */}
          {importParsedItems.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Назва розділу або шаблону</label>
              <input type="text" value={importSectionName} onChange={(e) => setImportSectionName(e.target.value)} className="w-full px-4 py-2 border rounded-lg" placeholder="Імпортовані позиції" />
            </div>
          )}
        </div>

        {/* Кнопки */}
        <div className="flex flex-col gap-2 px-4 sm:px-6 py-4 border-t bg-white flex-shrink-0">
          {mode === 'report' && onImport && (
            <button
              onClick={() => {
                const selected = importParsedItems.filter(i => i.selected);
                if (selected.length === 0) return;
                onImport(selected, importPriceType, importSectionName);
                resetAndClose();
              }}
              disabled={selectedCount === 0}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
            >
              Імпортувати {selectedCount > 0 && `(${selectedCount})`}
            </button>
          )}
          {templates && setTemplates && selectedCount > 0 && (
            <button onClick={saveAsTemplate} className="w-full px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90" style={{ backgroundColor: '#163248' }}>
              <Layers className="w-4 h-4 inline mr-1.5" />
              Зберегти як шаблон
            </button>
          )}
          {user && selectedCount > 0 && (
            <button onClick={saveToDirectory} className="w-full px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90" style={{ backgroundColor: '#f97316' }}>
              <Bookmark className="w-4 h-4 inline mr-1.5" />
              Зберегти в довідник
            </button>
          )}
          {setFilledCards && user && selectedCount > 0 && (
            <button onClick={saveAsTender} className="w-full px-4 py-2.5 rounded-lg text-sm font-medium bg-white hover:bg-gray-50 transition-colors border-2" style={{ color: '#163248', borderColor: '#163248' }}>
              <Link2 className="w-4 h-4 inline mr-1.5" />
              Зберегти як тендер
            </button>
          )}
          <button onClick={resetAndClose} className="w-full px-4 py-2.5 border rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50">
            Скасувати
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportModal;
